import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowLeft, Eye, Pencil, ShieldCheck, FileText, ExternalLink,
  ChevronLeft, ChevronRight,
  SquareUserRound, Sparkles, Award, Briefcase, GraduationCap,
  CheckCircle, Code, Globe, Heart,
} from 'lucide-react';
import {
  type PipelineCandidate,
  type DomainType,
} from '../../services/api';
import { invalidateCache } from '../../utils/apiCache';
import { SoftwareTalent } from '../../types';
import DeveloperProfile from '../../components/DeveloperProfile';
import { CandidateDetailModal } from './CandidateDetailModal';
import { ProfileEditorModal } from './ProfileEditorModal';
import { isAcademicDomain } from '../../config/appConfig';
import { cn } from '../../lib/utils';

export type DetailTab = 'view' | 'edit' | 'fraud-check';

interface CandidateUnifiedModalProps {
  profile: PipelineCandidate;
  allProfiles: PipelineCandidate[];
  initialTab?: DetailTab;
  onClose: () => void;
  onApprove: () => void;
  onRemove: () => void;
  onNavigate: (p: PipelineCandidate) => void;
  actionLoading?: boolean;
  onEditorSaved?: () => void;
  scrollToField?: string;
  initialEditorTab?: string;
}

function toTalent(p: PipelineCandidate): SoftwareTalent {
  return {
    id: Number(p.talentId),
    name: p.name || p.talentId,
    country: '',
    yearOfExperience: 0,
    skills: [],
    domain: p.domain?.toUpperCase(),
  };
}

const SECTION_NAV_ITEMS: { id: string; label: string; icon: React.ElementType }[] = [
  { id: 'info', label: 'Info', icon: SquareUserRound },
  { id: 'highlights', label: 'Highlights', icon: Sparkles },
  { id: 'publications', label: 'Publications', icon: FileText },
  { id: 'vetting', label: 'Vetting', icon: Award },
  { id: 'experience', label: 'Experience', icon: Briefcase },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'certifications', label: 'Certifications', icon: CheckCircle },
  { id: 'projects', label: 'Projects', icon: Code },
  { id: 'languages', label: 'Languages', icon: Globe },
  { id: 'hobbies', label: 'Hobbies', icon: Heart },
];

const STEM_SECTION_NAV_ITEMS: { id: string; label: string; icon: React.ElementType }[] = [
  { id: 'info', label: 'Info', icon: SquareUserRound },
  { id: 'highlights', label: 'Highlights', icon: Sparkles },
  { id: 'vetting', label: 'Vetting', icon: Award },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'experience', label: 'Experience', icon: Briefcase },
  { id: 'publications', label: 'Publications', icon: FileText },
  { id: 'certifications', label: 'Certifications', icon: CheckCircle },
  { id: 'projects', label: 'Projects', icon: Code },
  { id: 'languages', label: 'Languages', icon: Globe },
  { id: 'hobbies', label: 'Hobbies', icon: Heart },
];

const TABS: { key: DetailTab; label: string; icon: React.ElementType }[] = [
  { key: 'view', label: 'View', icon: Eye },
  { key: 'edit', label: 'Edit', icon: Pencil },
  { key: 'fraud-check', label: 'Fraud Check', icon: ShieldCheck },
];

export function CandidateUnifiedModal({
  profile,
  allProfiles,
  initialTab = 'view',
  onClose,
  onApprove,
  onRemove,
  onNavigate,
  actionLoading,
  onEditorSaved,
  scrollToField,
  initialEditorTab,
}: CandidateUnifiedModalProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab);
  const domain = (profile.domain?.toUpperCase() || 'SWE') as DomainType;
  const talent = toTalent(profile);
  const allTalent = allProfiles.map(toTalent);
  const sectionNavItems = isAcademicDomain(domain) ? STEM_SECTION_NAV_ITEMS : SECTION_NAV_ITEMS;

  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState('info');
  const [availableSections, setAvailableSections] = useState<string[]>(['info']);
  const availableSectionsRef = useRef<string[]>(['info']);
  const [sectionLabelOverrides, setSectionLabelOverrides] = useState<Record<string, string>>({});

  const ckey = (p: PipelineCandidate) => `${p.talentId}::${p.domain}`;
  const currentIndex = allProfiles.findIndex((p) => ckey(p) === ckey(profile));
  const prevProfile = currentIndex > 0 ? allProfiles[currentIndex - 1] : null;
  const nextProfile = currentIndex < allProfiles.length - 1 ? allProfiles[currentIndex + 1] : null;

  // Prevent body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Keyboard: Escape, Arrow keys
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const editable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement)?.isContentEditable;
      if (e.key === 'Escape') onClose();
      else if (editable) return;
      else if (e.key === 'ArrowLeft' && prevProfile) { e.preventDefault(); onNavigate(prevProfile); }
      else if (e.key === 'ArrowRight' && nextProfile) { e.preventDefault(); onNavigate(nextProfile); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prevProfile, nextProfile, onClose, onNavigate]);

  // Forward wheel from overlay to scroll container
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const handleWheel = (e: WheelEvent) => {
      const container = scrollContainerRef.current;
      if (!container || container.contains(e.target as Node)) return;
      e.preventDefault();
      container.scrollTop += e.deltaY;
    };
    overlay.addEventListener('wheel', handleWheel, { passive: false });
    return () => overlay.removeEventListener('wheel', handleWheel);
  }, []);

  // Section scroll tracking (View tab)
  const scrollToSection = useCallback((sectionId: string) => {
    const el = scrollContainerRef.current?.querySelector(`[data-section-id="${sectionId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'view') return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const top = container.scrollTop + 80;
      let current = availableSections[0] || 'info';
      for (const id of availableSections) {
        const el = container.querySelector(`[data-section-id="${id}"]`);
        if (el) {
          const rect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const elTop = rect.top - containerRect.top + container.scrollTop;
          if (elTop <= top) current = id;
        }
      }
      setActiveSection(current);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeTab, talent.id, availableSections]);

  // Detect available sections (View tab)
  useEffect(() => {
    if (activeTab !== 'view') return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const checkSections = () => {
      const sectionEls = container.querySelectorAll('[data-section-id]');
      const idsSet = new Set<string>();
      const labelMap: Record<string, string> = {};
      sectionEls.forEach((el) => {
        const id = el.getAttribute('data-section-id');
        if (id) {
          idsSet.add(id);
          const customLabel = el.getAttribute('data-section-label');
          if (customLabel) labelMap[id] = customLabel;
        }
      });
      if (idsSet.size > 0) {
        const ordered = sectionNavItems.filter((item) => idsSet.has(item.id)).map((item) => item.id);
        const same = ordered.length === availableSectionsRef.current.length && ordered.every((id, i) => id === availableSectionsRef.current[i]);
        if (!same) {
          availableSectionsRef.current = ordered;
          setAvailableSections(ordered);
        }
        setSectionLabelOverrides(labelMap);
      }
    };
    const timer = setTimeout(checkSections, 500);
    const observer = new MutationObserver(checkSections);
    observer.observe(container, { childList: true, subtree: true });
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [activeTab, talent.id, sectionNavItems]);

  const resumeUrl = `https://matching.turing.com/developer/${profile.talentId}/resume`;
  const matchingProfileUrl = `https://matching.turing.com/developer/${profile.talentId}`;

  return ReactDOM.createPortal(
    <div ref={overlayRef} className="fixed inset-0 z-50 flex items-stretch justify-center p-0 md:p-6 md:items-center">
      {/* Backdrop */}
      <div className="absolute left-0 top-0 bottom-0 w-16 md:w-20 bg-black/50" aria-hidden />
      <div className="absolute right-0 top-0 bottom-0 w-16 md:w-20 bg-black/50" aria-hidden />
      <div
        className="absolute left-16 md:left-20 right-16 md:right-20 top-0 bottom-0 bg-black/50"
        onClick={onClose}
        aria-hidden
      />

      {/* Prev/Next */}
      {prevProfile && (
        <button
          onClick={() => onNavigate(prevProfile)}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 shadow-lg border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
          aria-label="Previous candidate"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {nextProfile && (
        <button
          onClick={() => onNavigate(nextProfile)}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center rounded-full bg-white dark:bg-zinc-800 text-gray-700 dark:text-zinc-300 shadow-lg border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700 transition-colors"
          aria-label="Next candidate"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Modal */}
      <div
        className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl min-h-0 bg-white dark:bg-zinc-900 md:rounded-xl shadow-2xl overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Shared Header ── */}
        <div className="flex-shrink-0 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 shadow-sm z-10">
          <div className="px-6 py-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <button
                  type="button"
                  aria-label="Back to candidates"
                  onClick={onClose}
                  className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {profile.name || profile.talentId}
                    </h2>
                    <a
                      href={resumeUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <FileText className="h-4 w-4" aria-hidden="true" />
                      Resume
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs font-semibold text-gray-400 dark:text-zinc-500">
                    <span>{domain}</span>
                    <span>&middot;</span>
                    <span>
                      ID{' '}
                      <a
                        href={matchingProfileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 transition-colors hover:text-blue-700 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        {profile.talentId}
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-shrink-0 justify-end">
                <div className="inline-flex rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-100 dark:bg-zinc-800 p-1">
                  {TABS.map((t) => {
                    const Icon = t.icon;
                    const isActive = activeTab === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => setActiveTab(t.key)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors',
                          isActive
                            ? 'bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 shadow-sm'
                            : 'text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-zinc-100',
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section Nav (View tab only) ── */}
        {activeTab === 'view' && (
          <div className="flex-shrink-0 bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800 z-10 px-4 md:px-6">
            <div className="overflow-x-auto scrollbar-hide">
              <div className="flex items-center gap-0 min-w-max" role="tablist">
                {sectionNavItems
                  .filter((item) => item.id !== 'highlights' && availableSections.includes(item.id))
                  .map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        role="tab"
                        aria-selected={isActive}
                        className={cn(
                          'relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                          isActive
                            ? 'text-gray-900 dark:text-zinc-100'
                            : 'text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-300',
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{sectionLabelOverrides[item.id] || item.label}</span>
                        {isActive && (
                          <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-gray-900 dark:bg-zinc-100 rounded-full" />
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* ── Tab Content ── */}
        <div
          ref={scrollContainerRef}
          className={cn(
            'flex-1 min-h-0 overflow-hidden flex flex-col',
            activeTab === 'view' && 'overflow-y-auto overflow-x-hidden overscroll-contain',
          )}
          style={activeTab === 'view' ? { touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' } as React.CSSProperties : undefined}
        >
          {activeTab === 'view' && (
            <div className="px-4 py-4 md:px-6 md:py-6 min-h-full">
              <DeveloperProfile
                talentId={talent.id}
                domain={domain}
                onBack={onClose}
                searchTalent={talent}
                allTalent={allTalent}
                onNavigateTalent={(t) => {
                  const match = allProfiles.find((p) => Number(p.talentId) === t.id);
                  if (match) onNavigate(match);
                }}
                skipViewTracking
                hideHeader
                collapsibleSections
                bypassTenant
              />
            </div>
          )}

          {activeTab === 'edit' && (
            <ProfileEditorModal
              talentId={profile.talentId}
              domain={profile.domain}
              initialName={profile.name}
              onClose={onClose}
              onSaved={() => {
                invalidateCache(`dev-${profile.talentId}-${domain}`);
                onEditorSaved?.();
                setActiveTab('view');
              }}
              embedded
              scrollToField={scrollToField}
              initialTab={initialEditorTab as any}
            />
          )}

          {activeTab === 'fraud-check' && (
            <CandidateDetailModal
              profile={profile}
              onClose={onClose}
              onApprove={onApprove}
              onRemove={onRemove}
              loading={actionLoading}
              embedded
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
